import { useTranslation } from 'react-i18next';

export default function Filters({ facets, filters, onChange, onClear, activeCount }) {
  const { t } = useTranslation();

  function update(patch) {
    onChange({ ...filters, ...patch });
  }

  function toggleType(type) {
    const next = filters.types.includes(type)
      ? filters.types.filter((t) => t !== type)
      : [...filters.types, type];
    update({ types: next });
  }

  function toggleKeyword(kw) {
    const next = filters.keywords.includes(kw)
      ? filters.keywords.filter((k) => k !== kw)
      : [...filters.keywords, kw];
    update({ keywords: next });
  }

  return (
    <aside className="space-y-6">
      <header className="flex items-center justify-between">
        <h2 className="font-serif text-lg text-gold-light">{t('common.filter')}</h2>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-medium text-gold-light hover:underline"
          >
            {t('bibliography.clearAll')} ({activeCount})
          </button>
        )}
      </header>

      <Group label={t('bibliography.filter.type')}>
        <div className="flex flex-col gap-2">
          {facets.types.map((type) => (
            <label key={type} className="flex cursor-pointer items-center gap-2 text-sm text-ink-secondary">
              <input
                type="checkbox"
                checked={filters.types.includes(type)}
                onChange={() => toggleType(type)}
                className="h-4 w-4 accent-gold"
              />
              {t(`bibliography.types.${type}`)}
            </label>
          ))}
        </div>
      </Group>

      <Group label={t('bibliography.filter.author')}>
        <input
          type="text"
          placeholder={t('bibliography.filter.authorPlaceholder')}
          value={filters.author}
          onChange={(e) => update({ author: e.target.value })}
          className="w-full rounded-md border border-white/10 bg-navy-deep/60 px-3 py-2 text-sm text-cream placeholder:text-ink-muted focus:border-gold/50"
        />
      </Group>

      <Group label={t('bibliography.filter.year')}>
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            placeholder={facets.yearMin?.toString() ?? ''}
            value={filters.yearMin ?? ''}
            onChange={(e) => update({ yearMin: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="w-24 rounded-md border border-white/10 bg-navy-deep/60 px-2 py-1.5 text-sm text-cream focus:border-gold/50"
            aria-label={t('bibliography.filter.yearFrom')}
          />
          <span className="text-ink-muted">–</span>
          <input
            type="number"
            inputMode="numeric"
            placeholder={facets.yearMax?.toString() ?? ''}
            value={filters.yearMax ?? ''}
            onChange={(e) => update({ yearMax: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="w-24 rounded-md border border-white/10 bg-navy-deep/60 px-2 py-1.5 text-sm text-cream focus:border-gold/50"
            aria-label={t('bibliography.filter.yearTo')}
          />
        </div>
      </Group>

      <Group label={t('bibliography.filter.language')}>
        <select
          value={filters.language}
          onChange={(e) => update({ language: e.target.value })}
          className="w-full rounded-md border border-white/10 bg-navy-deep/60 px-3 py-2 text-sm text-cream focus:border-gold/50"
        >
          <option value="">{t('bibliography.filter.allLanguages')}</option>
          {facets.languages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
      </Group>

      <Group label={t('bibliography.filter.keywords')}>
        {filters.keywords.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {filters.keywords.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => toggleKeyword(k)}
                className="flex items-center gap-1 rounded-full border border-gold/40 bg-navy-mid/40 px-2.5 py-0.5 text-xs text-gold-light hover:border-gold"
              >
                {k} <span className="text-ink-muted">×</span>
              </button>
            ))}
          </div>
        )}
        <div className="max-h-56 overflow-y-auto pr-1">
          <div className="flex flex-col gap-1">
            {facets.keywords.slice(0, 80).map(({ keyword, count }) => {
              if (filters.keywords.includes(keyword)) return null;
              return (
                <button
                  key={keyword}
                  type="button"
                  onClick={() => toggleKeyword(keyword)}
                  className="flex items-center justify-between text-left text-xs text-ink-secondary hover:text-gold-light"
                >
                  <span>{keyword}</span>
                  <span className="text-ink-muted">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Group>
    </aside>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <h3 className="mb-2 text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-ink-muted">
        {label}
      </h3>
      {children}
    </div>
  );
}
